import React, { useEffect, useRef, useState } from 'react';
import { makeStyles } from '@mui/styles';
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CircularProgress,
  Grid,
  Stack
} from '@mui/material';
import PropTypes from 'prop-types';
import InfiniteScroll from 'react-infinite-scroller';
import { capturePHEvents } from '../../../../analytics/posthog.analytics';
import { PH_ADD_RESOURCE_CLICK } from '../../../../utils/posthog.eventNames';
import SelectedResources from '../../../../components/SelectedResources';
import AddResourceModal from '../../../../components/AddResourceModal';
import FileContainer from '../../../../components/FileContainer';
import ThreeDotIcon from '../../../../svg/ThreeDotIcon';
import ColorBookIcon from '../../../../svg/ColorBookIcon';
import palette from '../../../../theme/palette';
import ColorFilesSvg from '../../../../svg/colorFile.svg';
import ColorLinkSvg from '../../../../svg/colorLink.svg';
import YoutubeSvg from '../../../../svg/colorYoutube.svg';
import { deleteResource, fetchResourcesByClassId } from '../../../../service/resource.service';
import { API_LIMIT_DATA } from '../../../../utils/constants';

const useStyles = makeStyles({
  containerStyle: {
    marginTop: 15
  },
  characterContainer: {
    textAlign: 'end',
    color: palette.grey[500]
  },
  emptyResources: {
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    paddingTop: 50,
    paddingBottom: 50
  },
  resourceContainer: {
    borderRadius: 5,
    // border: '1px solid rgba(0, 0, 0, 0.08)',
    backgroundColor: 'white',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    padding: '20px 0',
    cursor: 'pointer'
  },
  resourceTitle: {
    marginTop: '15px'
  }
});

export default function TeacherResources({ classId }) {
  const classes = useStyles();
  const [resources, setResources] = useState([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedRes, setSelRes] = useState('');
  const [isLoading, setLoading] = useState(true);
  const [offset, setOffset] = useState(1);
  const [hasMore, setHasMore] = useState();
  const [editResObj, setEditRes] = useState(null);
  const [isResSecLoading, setResSecLoading] = useState(false);
  const singleResRef = useRef();
  const [isDataLoading, setIsDataLoading] = useState(false);

  useEffect(() => {
    async function fetchRes() {
      const res = await fetchResourcesByClassId(classId, API_LIMIT_DATA, offset);
      if (res) {
        setResources(res.results);
        setHasMore(res.results.length === API_LIMIT_DATA);
        setOffset(offset + 1);
      }
      setLoading(false);
    }
    fetchRes();
  }, []);

  const fetchNextData = async () => {
    if (hasMore) {
      const res = await fetchResourcesByClassId(classId, API_LIMIT_DATA, offset);
      if (res) {
        setResources((preResource) => [...preResource, ...res.results]);
        setHasMore(res.results.length === API_LIMIT_DATA);
        setOffset(offset + 1);
      }
      setIsDataLoading(false);
    }
  };

  const handleDelete = async (id) => {
    await deleteResource(id).then(() => {
      const findResIndex = resources.findIndex((reso) => reso.id === id);
      if (findResIndex > -1) {
        setResources([...resources.slice(0, findResIndex), ...resources.slice(findResIndex + 1)]);
      }
    });
  };

  const refetchResourceData = async () => {
    setResSecLoading(true);
    const res = await fetchResourcesByClassId(classId, API_LIMIT_DATA, 1);
    if (res) {
      setResources(res.results);
      setHasMore(res.results.length === API_LIMIT_DATA);
      setOffset(1 + 1);
      setResSecLoading(false);
    } else {
      setResSecLoading(false);
    }

    if (singleResRef.current) {
      singleResRef.current.refreshData();
    }
  };

  return isLoading ? (
    <div style={{ textAlign: 'center', padding: '20% 0' }}>
      <CircularProgress />
    </div>
  ) : (
    <div className={classes.containerStyle}>
      <Stack direction="row" justifyContent="flex-end">
        <Button
          variant="contained"
          onClick={() => {
            capturePHEvents(PH_ADD_RESOURCE_CLICK, { classId });
            setOpenDialog(true);
          }}
        >
          + New Resources
        </Button>
      </Stack>
      {resources.length > 0 ? (
        <div style={{ marginTop: 15 }}>
          {selectedRes === '' && (
            <>
              <Grid container spacing={2}>
                <Grid item xs={6} sm={4} md={4}>
                  <Card>
                    <CardContent
                      className={classes.resourceContainer}
                      onClick={() => setSelRes('file')}
                    >
                      <img src={ColorFilesSvg} alt="files" />
                      <p className={classes.resourceTitle}>Files</p>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={6} sm={4} md={4}>
                  <Card>
                    <CardContent
                      className={classes.resourceContainer}
                      onClick={() => setSelRes('link')}
                    >
                      <img src={ColorLinkSvg} alt="link" />
                      <p className={classes.resourceTitle}>Links</p>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={6} sm={4} md={4}>
                  <Card>
                    <CardContent
                      className={classes.resourceContainer}
                      onClick={() => setSelRes('video')}
                    >
                      <img src={YoutubeSvg} alt="youtube" />
                      <p className={classes.resourceTitle}>Youtube Video</p>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
              <Card
                style={{ marginTop: 15, maxHeight: '500px', overflowY: 'auto' }}
                id="scrollableCard"
              >
                <CardHeader title="Recently uploaded" />
                <CardContent>
                  {isResSecLoading ? (
                    <div style={{ textAlign: 'center', padding: '15% 0' }}>
                      <CircularProgress />
                    </div>
                  ) : (
                    <InfiniteScroll
                      pageStart={0}
                      loadMore={() => {
                        if (hasMore && !isDataLoading) {
                          fetchNextData();
                          setIsDataLoading(true);
                        }
                      }}
                      hasMore={hasMore}
                      getScrollParent={() => document.getElementById('scrollableCard')}
                      loader={
                        <div className="loader" key={0}>
                          Loading ...
                        </div>
                      }
                      useWindow={false}
                    >
                      {resources.map((ru) => (
                        <FileContainer
                          id={ru.id}
                          key={ru.id}
                          file={ru.file}
                          title={ru.name}
                          type={ru.type}
                          date={ru.createdAt}
                          rightIcon={<ThreeDotIcon />}
                          onRenamePress={() => {
                            setEditRes(ru);
                            setOpenDialog(true);
                          }}
                          onDeletePress={() => handleDelete(ru.id)}
                          onDownloadPress={() => {}}
                          onMoveCopyPress={() => {}}
                        />
                      ))}
                    </InfiniteScroll>
                  )}
                </CardContent>
              </Card>
            </>
          )}
          {/* {selectedRes === 'folder' && (
                <SelectedResources
                  title="Folder"
                  contents={FOLDER_DATA}
                  onBack={() => setSelRes('')}
                />
              )} */}
          {selectedRes === 'file' && (
            <SelectedResources
              title="Files"
              ref={singleResRef}
              classId={classId}
              type="file"
              onBack={() => setSelRes('')}
              editResource={(ru) => {
                setEditRes(ru);
                setOpenDialog(true);
              }}
              deleteResource={(id) => handleDelete(id)}
            />
          )}
          {selectedRes === 'link' && (
            <SelectedResources
              ref={singleResRef}
              title="Links"
              type="link"
              classId={classId}
              onBack={() => setSelRes('')}
              editResource={(ru) => {
                setEditRes(ru);
                setOpenDialog(true);
              }}
              deleteResource={(id) => handleDelete(id)}
            />
          )}
          {selectedRes === 'video' && (
            <SelectedResources
              title="Youtube Video"
              ref={singleResRef}
              type="youtube"
              classId={classId}
              onBack={() => setSelRes('')}
              editResource={(ru) => {
                setEditRes(ru);
                setOpenDialog(true);
              }}
              deleteResource={(id) => handleDelete(id)}
            />
          )}
        </div>
      ) : (
        <div className={classes.emptyResources}>
          <ColorBookIcon style={{ margin: '20px 0' }} />
          <h3 style={{ marginBottom: 10 }}>No Resource added yet!</h3>
          <p style={{ color: palette.grey[600] }}>
            Please add resources by clicking the button on right
          </p>
          <p style={{ color: palette.grey[600] }}>
            These resources will be sent to all the students
          </p>
        </div>
      )}
      <AddResourceModal
        editObj={editResObj}
        classId={classId}
        open={openDialog}
        onClose={() => {
          setOpenDialog(false);
          // refetchResourceData();
          setEditRes(null);
        }}
        fetchData={() => refetchResourceData()}
      />
    </div>
  );
}

TeacherResources.propTypes = {
  classId: PropTypes.string
};
